import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import ResultDownloadButton from './ResultDownloadComponent';

// ============================================================================
// ACADEMIC CONTEXT SELECTOR
// ============================================================================

const AcademicContextSelector = ({ tenantId, onContextChange, initialContext }) => {
  // State for dropdown options
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [terms, setTerms] = useState([]);
  
  // State for selections
  const [programTypeId, setProgramTypeId] = useState(initialContext?.programTypeId || '');
  const [programId, setProgramId] = useState(initialContext?.programId || '');
  const [academicYearId, setAcademicYearId] = useState(initialContext?.academicYearId || '');
  const [semesterId, setSemesterId] = useState(initialContext?.semesterId || '');
  const [termId, setTermId] = useState(initialContext?.termId || '');
  
  // UI state
  const [isSemesterBased, setIsSemesterBased] = useState(initialContext?.isSemesterBased ?? null);
  const [error, setError] = useState('');
  
  // Loading states
  const [loadingProgramTypes, setLoadingProgramTypes] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingAcademicYears, setLoadingAcademicYears] = useState(false);
  const [loadingSemestersTerms, setLoadingSemestersTerms] = useState(false);

  // Helper to check if academic year is completed
  const isAcademicYearCompleted = useCallback((academicYear) => {
    return academicYear?.status === 'completed' || 
           academicYear?.isCompleted === true ||
           academicYear?.academicYearStatus === 'completed';
  }, []);

  // Helper to check if semester is completed
  const isSemesterCompleted = useCallback((semester) => {
    return semester?.status === 'completed' || 
           semester?.isCompleted === true ||
           semester?.semesterStatus === 'completed';
  }, []);

  // Helper to check if term is completed
  const isTermCompleted = useCallback((term) => {
    return term?.status === 'completed' || 
           term?.isCompleted === true ||
           term?.termStatus === 'completed';
  }, []);

  // Fetch program types
  useEffect(() => {
    if (!tenantId) return;
    setLoadingProgramTypes(true);
    api.get('/api/program-types', { params: { tenantId, status: 'active' } })
      .then(res => {
        setProgramTypes(res.data.programTypes || []);
      })
      .catch(() => setError('Failed to load program types'))
      .finally(() => setLoadingProgramTypes(false));
  }, [tenantId]);

  // Fetch programs when program type changes
  useEffect(() => {
    if (!tenantId || !programTypeId) {
      setPrograms([]);
      return;
    }
    setLoadingPrograms(true);
    api.get('/api/programs', { params: { tenantId, programTypeId, status: 'active' } })
      .then(res => {
        setPrograms(res.data.programs || []);
      })
      .catch(() => setError('Failed to load programs'))
      .finally(() => setLoadingPrograms(false));
  }, [tenantId, programTypeId]);

  // Fetch academic years (active and completed)
  useEffect(() => {
    if (!tenantId) return;
    setLoadingAcademicYears(true);
    Promise.all([
      api.get('/api/academic-year/all-active', { params: { tenantId } }),
      api.get('/api/academic-year/all-completed', { params: { tenantId } })
    ])
      .then(([activeRes, completedRes]) => {
        const activeYears = activeRes.data.academicYears || activeRes.data.activeAcademicYears || [];
        const completedYears = completedRes.data.academicYears || [];
        setAcademicYears([...activeYears, ...completedYears]);
      })
      .catch(() => setError('Failed to load academic years'))
      .finally(() => setLoadingAcademicYears(false));
  }, [tenantId]);

  // Fetch semesters/terms when academic year changes
  useEffect(() => {
    if (!academicYearId) {
      setSemesters([]);
      setTerms([]);
      setIsSemesterBased(null);
      return;
    }

    setLoadingSemestersTerms(true);
    setSemesters([]);
    setTerms([]);
    setIsSemesterBased(null);
    
    api.get(`/api/classManager/semesters/${academicYearId}`)
      .then(res => {
        const sems = res.data.semesters || [];
        if (sems.length > 0) {
          setSemesters(sems);
          setIsSemesterBased(true);
        } else {
          api.get(`/api/academic-year/${academicYearId}/terms`, { params: { tenantId } })
            .then(res2 => {
              const trms = res2.data.terms || [];
              setTerms(trms);
              setIsSemesterBased(false);
            })
            .catch(() => setError('Failed to load terms'));
        }
      })
      .catch(() => {
        api.get(`/api/academic-year/${academicYearId}/terms`, { params: { tenantId } })
          .then(res2 => {
            const trms = res2.data.terms || [];
            setTerms(trms);
            setIsSemesterBased(false);
          })
          .catch(() => setError('Failed to load semesters/terms'));
      })
      .finally(() => setLoadingSemestersTerms(false));
  }, [academicYearId, tenantId]);

  const handleProgramTypeChange = (value) => {
    setProgramTypeId(value);
    setProgramId('');
    setAcademicYearId('');
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
    onContextChange(null);
  };

  const handleProgramChange = (value) => {
    setProgramId(value);
    setAcademicYearId('');
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
    onContextChange(null);
  };

  const handleAcademicYearChange = (value) => {
    setAcademicYearId(value);
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
    onContextChange(null);
  };

  const handleSemesterChange = (value) => {
    setSemesterId(value);
    setTimeout(() => {
      const selectedProgramType = programTypes.find(pt => pt._id === programTypeId);
      const selectedProgram = programs.find(p => p._id === programId);
      const selectedAcademicYear = academicYears.find(y => y._id === academicYearId);
      const selectedSemester = semesters.find(s => s._id === value);
      
      onContextChange({
        programTypeId,
        programTypeName: selectedProgramType?.name || '',
        programId,
        programName: selectedProgram?.name || '',
        academicYearId,
        academicYearName: selectedAcademicYear?.name || selectedAcademicYear?.academicYear || '',
        semesterId: value,
        semesterName: selectedSemester?.name || selectedSemester?.semesterLabel || `Semester ${selectedSemester?.semesterNumber}` || '',
        termId: null,
        termName: '',
        isSemesterBased: true,
        levelId: value,
        levelName: selectedSemester?.name || selectedSemester?.semesterLabel || `Semester ${selectedSemester?.semesterNumber}` || ''
      });
    }, 100);
  };

  const handleTermChange = (value) => {
    setTermId(value);
    setTimeout(() => {
      const selectedProgramType = programTypes.find(pt => pt._id === programTypeId);
      const selectedProgram = programs.find(p => p._id === programId);
      const selectedAcademicYear = academicYears.find(y => y._id === academicYearId);
      const selectedTerm = terms.find(t => t._id === value);
      
      onContextChange({
        programTypeId,
        programTypeName: selectedProgramType?.name || '',
        programId,
        programName: selectedProgram?.name || '',
        academicYearId,
        academicYearName: selectedAcademicYear?.name || selectedAcademicYear?.academicYear || '',
        semesterId: null,
        semesterName: '',
        termId: value,
        termName: selectedTerm?.name || selectedTerm?.termLabel || `Term ${selectedTerm?.termNumber}` || '',
        isSemesterBased: false,
        levelId: value,
        levelName: selectedTerm?.name || selectedTerm?.termLabel || `Term ${selectedTerm?.termNumber}` || ''
      });
    }, 100);
  };

  const getAcademicYearLabel = (ayId) => {
    const ay = academicYears.find(y => y._id === ayId);
    if (!ay) return '';
    const isCompleted = isAcademicYearCompleted(ay);
    return `${ay.name || ay.academicYear}${isCompleted ? ' (Completed)' : ''}`;
  };

  const getSemesterLabel = (semId) => {
    const sem = semesters.find(s => s._id === semId);
    if (!sem) return '';
    const isCompleted = isSemesterCompleted(sem);
    return `${sem.name || sem.semesterLabel || `Semester ${sem.semesterNumber}`}${isCompleted ? ' (Completed)' : ''}`;
  };

  const getTermLabel = (tId) => {
    const term = terms.find(t => t._id === tId);
    if (!term) return '';
    const isCompleted = isTermCompleted(term);
    return `${term.name || term.termLabel || `Term ${term.termNumber}`}${isCompleted ? ' (Completed)' : ''}`;
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="bg-indigo-900 px-4 py-4 md:px-5">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-white/10">
            <svg className="h-5 w-5 text-indigo-200" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422A12.083 12.083 0 0112 21.5c-4.418 0-8-1.79-8-4V9" />
            </svg>
          </div>
          <div>
            <h2 className="text-base md:text-lg font-bold text-white">Academic Context Selection</h2>
            <p className="text-xs text-indigo-200">Select program details and academic intervals below</p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-5">
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5">
            <svg className="h-4 w-4 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-sm font-medium text-red-700">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
              <span className="grid h-4 w-4 place-items-center rounded bg-indigo-100 text-[10px] text-indigo-700">1</span>
              Program Type <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={programTypeId}
                onChange={(e) => handleProgramTypeChange(e.target.value)}
                disabled={loadingProgramTypes}
                className="w-full appearance-none rounded-xl border border-slate-300 bg-white pl-3 pr-8 py-2.5 text-sm text-slate-800 transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent disabled:bg-slate-50"
              >
                <option value="">Select Program Type</option>
                {programTypes.map(pt => (
                  <option key={pt._id} value={pt._id}>{pt.name}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
              <span className="grid h-4 w-4 place-items-center rounded bg-indigo-100 text-[10px] text-indigo-700">2</span>
              Program <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={programId}
                onChange={(e) => handleProgramChange(e.target.value)}
                disabled={!programTypeId || loadingPrograms}
                className="w-full appearance-none rounded-xl border border-slate-300 bg-white pl-3 pr-8 py-2.5 text-sm text-slate-800 transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">Select Program</option>
                {programs.map(p => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
              <span className="grid h-4 w-4 place-items-center rounded bg-indigo-100 text-[10px] text-indigo-700">3</span>
              Academic Year <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={academicYearId}
                onChange={(e) => handleAcademicYearChange(e.target.value)}
                disabled={!programId || loadingAcademicYears}
                className="w-full appearance-none rounded-xl border border-slate-300 bg-white pl-3 pr-8 py-2.5 text-sm text-slate-800 transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">Select Academic Year</option>
                {academicYears.map(ay => (
                  <option key={ay._id} value={ay._id}>
                    {getAcademicYearLabel(ay._id)}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
              </div>
            </div>
          </div>

          {academicYearId && isSemesterBased === true && (
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                <span className="grid h-4 w-4 place-items-center rounded bg-emerald-100 text-[10px] text-emerald-700">4</span>
                Semester <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={semesterId}
                  onChange={(e) => handleSemesterChange(e.target.value)}
                  disabled={!academicYearId || semesters.length === 0 || loadingSemestersTerms}
                  className="w-full appearance-none rounded-xl border border-slate-300 bg-white pl-3 pr-8 py-2.5 text-sm text-slate-800 transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">Select Semester</option>
                  {semesters.map(sem => (
                    <option key={sem._id} value={sem._id}>
                      {getSemesterLabel(sem._id)}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>
            </div>
          )}

          {academicYearId && isSemesterBased === false && (
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                <span className="grid h-4 w-4 place-items-center rounded bg-emerald-100 text-[10px] text-emerald-700">4</span>
                Term <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={termId}
                  onChange={(e) => handleTermChange(e.target.value)}
                  disabled={!academicYearId || terms.length === 0 || loadingSemestersTerms}
                  className="w-full appearance-none rounded-xl border border-slate-300 bg-white pl-3 pr-8 py-2.5 text-sm text-slate-800 transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">Select Term</option>
                  {terms.map(term => (
                    <option key={term._id} value={term._id}>
                      {getTermLabel(term._id)}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>
            </div>
          )}
        </div>

        {programTypeId && programId && academicYearId && ((isSemesterBased === true && semesterId) || (isSemesterBased === false && termId)) && (
          <div className="mt-4 border-t border-slate-100 pt-4 animate-fadeIn">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Active Configuration
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <div className="bg-white p-2.5 rounded-lg border border-slate-200">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase block">Program Type</span>
                  <p className="font-semibold text-slate-800 truncate">{programTypes.find(pt => pt._id === programTypeId)?.name || '-'}</p>
                </div>
                <div className="bg-white p-2.5 rounded-lg border border-slate-200">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase block">Program</span>
                  <p className="font-semibold text-slate-800 truncate">{programs.find(p => p._id === programId)?.name || '-'}</p>
                </div>
                <div className="bg-white p-2.5 rounded-lg border border-slate-200">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase block">Academic Year</span>
                  <p className="font-semibold text-slate-800 truncate">{getAcademicYearLabel(academicYearId)}</p>
                </div>
                <div className="bg-white p-2.5 rounded-lg border border-slate-200">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase block">{isSemesterBased ? 'Semester' : 'Term'}</span>
                  <p className="font-semibold text-slate-800 truncate">
                    {isSemesterBased ? getSemesterLabel(semesterId) : getTermLabel(termId)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PROMOTION BATCHES COMPONENT WITH ROLLBACK & DOWNLOAD
// ============================================================================

const PromotionBatches = ({ tenantId }) => {
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
  const [showBatchDetails, setShowBatchDetails] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 50, skip: 0 });
  const [academicContext, setAcademicContext] = useState(null);
  const [hasSelectedContext, setHasSelectedContext] = useState(false);

  // ===== ROLLBACK STATE =====
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const fetchBatches = useCallback(async () => {
    if (!tenantId || !academicContext) {
      setBatches([]);
      setPagination({ total: 0, limit: 50, skip: 0 });
      return;
    }

    setLoading(true);
    setError('');
    setShowBatchDetails(false);
    setSelectedBatchDetails(null);

    try {
      const params = {
        tenantId,
        programTypeId: academicContext.programTypeId,
        programId: academicContext.programId,
        academicYearId: academicContext.academicYearId,
        limit: pagination.limit,
        skip: pagination.skip
      };

      if (academicContext.levelId) {
        params.levelId = academicContext.levelId;
        params.isSemesterBased = academicContext.isSemesterBased;
      }

      const res = await api.get('/api/coe/promotion-batches', { params });
      const data = res.data.data || {};
      setBatches(data.batches || []);
      setPagination(data.pagination || { total: 0, limit: 50, skip: 0 });
      setHasSelectedContext(true);
    } catch (err) {
      setError('Failed to load promotion batches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, academicContext, pagination.limit, pagination.skip]);

  const fetchBatchDetails = useCallback(async (batchId) => {
    if (!tenantId || !batchId) return;

    setLoading(true);
    setError('');
    setShowBatchDetails(true);

    try {
      const res = await api.get(`/api/coe/promotion-batches/${batchId}`, {
        params: { tenantId }
      });
      setSelectedBatchDetails(res.data.data);
    } catch (err) {
      setError('Failed to load batch details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const handleContextChange = (context) => {
    setAcademicContext(context);
    setBatches([]);
    setPagination(prev => ({ ...prev, skip: 0 }));
    setHasSelectedContext(false);
    setShowBatchDetails(false);
    setSelectedBatchDetails(null);
  };

  useEffect(() => {
    if (academicContext) {
      fetchBatches();
    }
  }, [academicContext, fetchBatches]);

  const handlePageChange = (newSkip) => {
    setPagination(prev => ({ ...prev, skip: newSkip }));
  };

  // ============================================================================
  // ROLLBACK FUNCTIONS
  // ============================================================================

  const handleRollbackBatch = (batchId) => {
    if (!tenantId || !batchId) {
      setError('Missing rollback configuration.');
      return;
    }

    setModalData({
      action: 'rollbackBatch',
      title: 'Undo Batch Process',
      message: `This action will reset all processed metrics saved under batch link "${batchId}". All student files will return to their prior academic state.`,
      confirmText: 'Reset Batch',
      cancelText: 'Cancel',
      data: { batchId }
    });
    setShowConfirmModal(true);
  };

  const handleRollbackIndividual = (studentId, batchId) => {
    if (!tenantId || !studentId) {
      setError('Student identification missing.');
      return;
    }

    const student = selectedBatchDetails?.students?.find(s => s.studentId === studentId);
    const studentName = student?.studentName || student?.studentUSN || 'Unknown Student';

    setModalData({
      action: 'rollbackIndividual',
      title: 'Undo Individual Student Process',
      message: `This will separate the student from this promotion roster. Academic parameters will return to their prior evaluation status.`,
      confirmText: 'Reset Student',
      cancelText: 'Cancel',
      data: { studentId, batchId, studentName }
    });
    setShowConfirmModal(true);
  };

  const confirmRollback = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (modalData.action === 'rollbackBatch') {
        await api.post('/api/coe/student-promotion/rollback', {
          tenantId,
          batchId: modalData.data.batchId,
          rolledBackBy: 'system',
          reason: `Batch rollback initiated by user`
        });
        
        setSuccess(`Batch ${modalData.data.batchId} has been successfully reset.`);
        await fetchBatches();
        
      } else if (modalData.action === 'rollbackIndividual') {
        await api.post('/api/coe/student-promotion/rollback-individual', {
          tenantId,
          studentId: modalData.data.studentId,
          rolledBackBy: 'system',
          reason: `Individual rollback for student ${modalData.data.studentName}`,
          batchId: modalData.data.batchId
        });
        
        setSuccess(`Student ${modalData.data.studentName} has been successfully reset.`);
        
        if (selectedBatchDetails) {
          await fetchBatchDetails(modalData.data.batchId);
        }
        await fetchBatches();
      }
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to rollback';
      setError(errorMessage);
      console.error('Rollback error:', err);
    } finally {
      setLoading(false);
      setModalData(null);
    }
  };

  // ============================================================================
  // UI HELPERS
  // ============================================================================

  const getStatusLabel = (status) => {
    const labels = {
      'PUBLISHED': { label: 'Published', color: 'bg-blue-50 text-blue-700 border-blue-200' },
      'PROMOTED': { label: 'Promoted', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      'PROVISIONAL': { label: 'Provisional', color: 'bg-purple-50 text-purple-700 border-purple-200' },
      'DRAFT': { label: 'Draft', color: 'bg-amber-50 text-amber-700 border-amber-200' },
      'ROLLED_BACK': { label: 'Rolled Back', color: 'bg-rose-50 text-rose-700 border-rose-200' }
    };
    return labels[status] || { label: status, color: 'bg-slate-50 text-slate-700 border-slate-200' };
  };

  const getDecisionLabel = (decision) => {
    const labels = {
      'PROMOTE': { label: 'Promote', color: 'bg-emerald-50 text-emerald-800 border-emerald-100' },
      'PROMOTE_WITH_BACKLOG': { label: 'Promote with Backlog', color: 'bg-amber-50 text-amber-800 border-amber-100' },
      'YEAR_BACK': { label: 'Year Back', color: 'bg-rose-50 text-rose-800 border-rose-100' },
      'NOT_EVALUATED': { label: 'Not Evaluated', color: 'bg-slate-50 text-slate-800 border-slate-100' }
    };
    return labels[decision] || { label: decision, color: 'bg-slate-50 text-slate-800 border-slate-100' };
  };

  const getLevelDisplayName = (metadata) => {
    if (!metadata) return 'N/A';
    if (metadata.levelName) return metadata.levelName;
    if (metadata.isSemesterBased) {
      return metadata.semesterName || `Semester ${metadata.semesterNumber || ''}`.trim() || 'Semester';
    }
    return metadata.termName || `Term ${metadata.termNumber || ''}`.trim() || 'Term';
  };

  const getProgramTypeDisplay = (metadata) => {
    if (!metadata) return 'N/A';
    return metadata.programTypeName || metadata.programTypeId || 'N/A';
  };

  const getProgramDisplay = (metadata) => {
    if (!metadata) return 'N/A';
    return metadata.programName || metadata.programId || 'N/A';
  };

  const getAcademicYearDisplay = (metadata) => {
    if (!metadata) return 'N/A';
    return metadata.academicYearName || metadata.academicYearId || 'N/A';
  };

  const isBatchPublished = (batch) => {
    const status = batch.batchStatus || batch.status;
    return status === 'PUBLISHED' || status === 'PROMOTED';
  };

  const isStudentPublished = (student) => {
    return student.isPublished || student.isPromoted || student.isProvisional;
  };

  // ============================================================================
  // RENDER CONFIRMATION MODAL
  // ============================================================================

  const renderConfirmModal = () => {
    if (!showConfirmModal || !modalData) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl border border-slate-200 space-y-4 animate-fadeIn">
          <div className="space-y-2">
            <h3 className="text-base font-bold text-slate-900">
              {modalData.title}
            </h3>
            <p className="text-sm text-slate-600 leading-normal">
              {modalData.message}
            </p>
            {modalData.action === 'rollbackIndividual' && modalData.data.studentName && (
              <div className="bg-slate-50 p-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700">
                Student Name: {modalData.data.studentName}
              </div>
            )}
            {modalData.action === 'rollbackBatch' && modalData.data.batchId && (
              <div className="bg-slate-50 p-2 rounded-lg border border-slate-200 text-xs font-mono text-slate-700">
                Batch Reference: {modalData.data.batchId}
              </div>
            )}
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs text-amber-800 font-medium">
                Warning: Reverting files cannot be undone. Records will update instantly.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => {
                setShowConfirmModal(false);
                setModalData(null);
              }}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition"
            >
              {modalData.cancelText || 'Cancel'}
            </button>
            <button
              onClick={confirmRollback}
              className="rounded-xl px-4 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 transition shadow-sm"
            >
              {modalData.confirmText || 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER MAIN MODULE
  // ============================================================================

  if (loading && batches.length === 0 && !hasSelectedContext) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="h-9 w-9 animate-spin rounded-full border-3 border-indigo-600 border-t-transparent" />
        <p className="text-sm font-semibold text-slate-500">Loading promotion batches...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-xl bg-rose-50 px-4 py-3 text-xs font-bold text-rose-800 border border-rose-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>Warning:</span>
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-rose-600 hover:text-rose-800 font-bold ml-2">✕</button>
        </div>
      )}

      {success && (
        <div className="rounded-xl bg-emerald-50 px-4 py-3 text-xs font-bold text-emerald-800 border border-emerald-200 flex items-center justify-between animate-fadeIn">
          <div className="flex items-center gap-2">
            <span>Success:</span>
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="text-emerald-600 hover:text-emerald-800 font-bold ml-2">✕</button>
        </div>
      )}

      <AcademicContextSelector
        tenantId={tenantId}
        onContextChange={handleContextChange}
        initialContext={academicContext}
      />

      {hasSelectedContext && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-4 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                Promotion Records Groups 
                <span className="text-xs font-normal text-slate-500">
                  ({pagination.total} entries listed)
                </span>
              </h3>
            </div>
            <button
              onClick={fetchBatches}
              disabled={loading}
              className="w-full sm:w-auto rounded-xl bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition border border-slate-300 disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh Records'}
            </button>
          </div>

          {/* Desktop Table View & Mobile Card Block Layout (Responsive) */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 block lg:table">
              <thead className="bg-slate-50 hidden lg:table-header-group">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Batch ID</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Program</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Academic Year</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Class</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Students Count</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Pass Yield</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Management</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white block lg:table-row-group">
                {batches.length === 0 ? (
                  <tr className="block lg:table-row">
                    <td colSpan="8" className="px-4 py-12 text-center text-sm text-slate-400 block lg:table-cell">
                      No promotion configurations available for this selection.
                    </td>
                  </tr>
                ) : (
                  batches.map((batch) => {
                    const statusInfo = getStatusLabel(batch.batchStatus || batch.status);
                    const isRollbackAllowed = batch.batchStatus === 'PUBLISHED' || 
                                              batch.batchStatus === 'PROMOTED' || 
                                              batch.batchStatus === 'PROVISIONAL';
                    const canDownload = isBatchPublished(batch);
                    const downloadContext = {
                      programTypeId: batch.programTypeId,
                      programId: batch.programId,
                      academicYearId: batch.academicYearId,
                      levelId: batch.levelId || batch.termId || batch.semesterId,
                      isSemesterBased: batch.isSemesterBased || false,
                      levelName: batch.levelName || batch.termName || batch.semesterName || 'N/A'
                    };
                    
                    return (
                      <tr key={batch.batchId} className="hover:bg-slate-50/50 transition block lg:table-row p-4 border-b border-slate-100 lg:border-none space-y-2 lg:space-y-0">
                        <td className="px-4 py-2 lg:py-3 text-xs font-mono font-bold text-slate-600 block lg:table-cell">
                          <span className="lg:hidden font-sans font-bold text-slate-400 block uppercase tracking-tight text-[10px]">Batch Reference</span>
                          {batch.batchId.slice(0, 16)}...
                        </td>
                        <td className="px-4 py-1 lg:py-3 text-sm font-semibold text-slate-800 block lg:table-cell">
                          <span className="lg:hidden font-sans font-bold text-slate-400 block uppercase tracking-tight text-[10px]">Program Title</span>
                          {batch.programName || batch.programCode}
                        </td>
                        <td className="px-4 py-1 lg:py-3 text-sm text-slate-600 block lg:table-cell">
                          <span className="lg:hidden font-sans font-bold text-slate-400 block uppercase tracking-tight text-[10px]">Academic Year</span>
                          {batch.academicYearName}
                        </td>
                        <td className="px-4 py-1 lg:py-3 text-sm text-slate-600 block lg:table-cell">
                          <span className="lg:hidden font-sans font-bold text-slate-400 block uppercase tracking-tight text-[10px]">Class Assignment</span>
                          {batch.className || '-'}
                        </td>
                        <td className="px-4 py-1 lg:py-3 text-sm lg:text-center font-bold text-slate-700 block lg:table-cell">
                          <span className="lg:hidden font-sans font-bold text-slate-400 block uppercase tracking-tight text-[10px]">Students Count</span>
                          {batch.totalStudents}
                        </td>
                        <td className="px-4 py-1 lg:py-3 lg:text-center block lg:table-cell">
                          <span className="lg:hidden font-sans font-bold text-slate-400 block uppercase tracking-tight text-[10px] mb-0.5">Status</span>
                          <span className={`inline-flex rounded-md px-2.5 py-0.5 text-xs font-bold border ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-1 lg:py-3 block lg:table-cell">
                          <span className="lg:hidden font-sans font-bold text-slate-400 block uppercase tracking-tight text-[10px] mb-0.5">Pass Yield Rate</span>
                          <div className="flex items-center lg:justify-center gap-2">
                            <span className="text-xs font-bold text-slate-700">{batch.promotionRate || 0}%</span>
                            <div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                              <div 
                                className={`h-full ${
                                  (batch.promotionRate || 0) >= 80 ? 'bg-emerald-600' :
                                  (batch.promotionRate || 0) >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                                }`}
                                style={{ width: `${batch.promotionRate || 0}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 lg:py-3 block lg:table-cell lg:text-center">
                          <span className="lg:hidden font-sans font-bold text-slate-400 block uppercase tracking-tight text-[10px] mb-1">Available Management Actions</span>
                          <div className="flex flex-col sm:flex-row lg:flex-col items-stretch lg:items-center gap-1.5">
                            <div className="flex items-center gap-1.5 w-full lg:justify-center">
                              <button
                                onClick={() => fetchBatchDetails(batch.batchId)}
                                className="flex-1 lg:flex-none rounded-xl bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition"
                              >
                                View List
                              </button>
                              {isRollbackAllowed && (
                                <button
                                  onClick={() => handleRollbackBatch(batch.batchId)}
                                  disabled={loading}
                                  className="flex-1 lg:flex-none rounded-xl bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-100 border border-rose-200 transition disabled:opacity-50"
                                >
                                  Reset
                                </button>
                              )}
                            </div>
                            {canDownload && (
                              <ResultDownloadButton
                                tenantId={tenantId}
                                context={downloadContext}
                                batchId={batch.batchId}
                                isBatch={true}
                                isPublished={true}
                                buttonText="Download Report"
                                buttonVariant="outline"
                                className="text-xs px-3 py-1 w-full justify-center"
                                onDownloadStart={() => console.log('Download starting:', batch.batchId)}
                                onDownloadComplete={() => console.log('Download complete:', batch.batchId)}
                                onDownloadError={(err) => console.error('Download issue:', err)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-200 px-4 py-3 gap-3">
              <div className="text-xs text-slate-500 font-medium">
                Showing entries {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} ledger configurations
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => handlePageChange(Math.max(0, pagination.skip - pagination.limit))}
                  disabled={pagination.skip === 0}
                  className="flex-1 sm:flex-none rounded-xl border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.skip + pagination.limit)}
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  className="flex-1 sm:flex-none rounded-xl border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================
          BATCH DETAILS MODAL WITH INDIVIDUAL ROLLBACK & DOWNLOAD
          ============================================================ */}
      {showBatchDetails && selectedBatchDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-2 sm:p-4 animate-fadeIn">
          <div className="w-full max-w-5xl h-full sm:h-auto max-h-[95vh] flex flex-col rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-4 sm:px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 flex-shrink-0">
              <div className="space-y-0.5">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-1.5">
                  Batch Students Ledger
                </h3>
                <p className="text-xs text-slate-500 font-mono tracking-tight truncate max-w-xs sm:max-w-md">{selectedBatchDetails.batchId}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {selectedBatchDetails.metadata?.isPublished && (
                  <ResultDownloadButton
                    tenantId={tenantId}
                    context={{
                      programTypeId: selectedBatchDetails.metadata?.programTypeId,
                      programId: selectedBatchDetails.metadata?.programId,
                      academicYearId: selectedBatchDetails.metadata?.academicYearId,
                      levelId: selectedBatchDetails.metadata?.levelId || selectedBatchDetails.metadata?.termId || selectedBatchDetails.metadata?.semesterId,
                      isSemesterBased: selectedBatchDetails.metadata?.isSemesterBased || false,
                      levelName: selectedBatchDetails.metadata?.levelName || 'N/A'
                    }}
                    batchId={selectedBatchDetails.batchId}
                    isBatch={true}
                    isPublished={true}
                    buttonText="Download Ledger"
                    buttonVariant="outline"
                    className="text-xs px-2.5 py-1.5"
                  />
                )}
                {(selectedBatchDetails.metadata?.isPublished || 
                  selectedBatchDetails.metadata?.isPromoted || 
                  selectedBatchDetails.metadata?.isProvisional) && (
                  <button
                    onClick={() => handleRollbackBatch(selectedBatchDetails.batchId)}
                    disabled={loading}
                    className="rounded-xl bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-100 border border-rose-200 transition disabled:opacity-50"
                  >
                    Reset Roster
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowBatchDetails(false);
                    setSelectedBatchDetails(null);
                  }}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Modal Body Scroll Container */}
            <div className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1">
              {/* Context Summary Meta Information */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Academic Framework & Settings
                </h4>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 text-xs">
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Policy Scheme</span>
                    <p className="font-semibold text-slate-800 truncate">{selectedBatchDetails.metadata?.policyName || 'N/A'}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Program Type</span>
                    <p className="font-semibold text-slate-800 truncate">{getProgramTypeDisplay(selectedBatchDetails.metadata)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Program</span>
                    <p className="font-semibold text-slate-800 truncate">{getProgramDisplay(selectedBatchDetails.metadata)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Academic Year</span>
                    <p className="font-semibold text-slate-800 truncate">{getAcademicYearDisplay(selectedBatchDetails.metadata)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Interval Level</span>
                    <p className="font-semibold text-slate-800 truncate">{getLevelDisplayName(selectedBatchDetails.metadata)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Roster Status</span>
                    <span className={`inline-block text-[11px] font-bold mt-0.5 ${
                      selectedBatchDetails.metadata?.isPromoted ? 'text-emerald-700' :
                      selectedBatchDetails.metadata?.isPublished ? 'text-blue-700' : 'text-amber-600'
                    }`}>
                      {selectedBatchDetails.metadata?.isPromoted ? 'Promoted' : selectedBatchDetails.metadata?.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Summary Dashboard Grid Metrics */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">
                <div className="rounded-xl border border-slate-200 bg-white p-3 text-center">
                  <span className="text-[11px] font-bold text-slate-400 uppercase">Total Students</span>
                  <p className="text-xl font-bold text-slate-800 mt-0.5">{selectedBatchDetails.summary.total}</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-3 text-center">
                  <span className="text-[11px] font-bold text-emerald-600 uppercase">Regular Promote</span>
                  <p className="text-xl font-bold text-emerald-800 mt-0.5">{selectedBatchDetails.summary.promoted}</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-3 text-center">
                  <span className="text-[11px] font-bold text-amber-600 uppercase">Promote Backlog</span>
                  <p className="text-xl font-bold text-amber-700 mt-0.5">{selectedBatchDetails.summary.promotedWithBacklog}</p>
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50/30 p-3 text-center">
                  <span className="text-[11px] font-bold text-rose-600 uppercase">Year Back Detention</span>
                  <p className="text-xl font-bold text-rose-700 mt-0.5">{selectedBatchDetails.summary.yearBack}</p>
                </div>
                <div className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-3 text-center col-span-2 sm:col-span-4 lg:col-span-1">
                  <span className="text-[11px] font-bold text-indigo-600 uppercase">Roster Pass Rate</span>
                  <p className="text-xl font-bold text-indigo-700 mt-0.5">
                    {selectedBatchDetails.summary.total > 0 ? Math.round((selectedBatchDetails.summary.promoted / selectedBatchDetails.summary.total) * 100) : 0}%
                  </p>
                </div>
              </div>

              {/* Passing Criteria Parameters Summary */}
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/30">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Applied Evaluation Bounds</h4>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 text-xs text-slate-700">
                  <div>
                    <span className="text-slate-400 block font-medium">Minimum Aggregate</span>
                    <p className="font-bold text-slate-800 mt-0.5">{selectedBatchDetails.metadata?.passingCriteria?.overallPassingPercentage || 40}%</p>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Minimum Course %</span>
                    <p className="font-bold text-slate-800 mt-0.5">{selectedBatchDetails.metadata?.passingCriteria?.subjectPassingPercentage || 40}%</p>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Attendance Bound</span>
                    <p className="font-bold text-slate-800 mt-0.5">{selectedBatchDetails.metadata?.passingCriteria?.attendanceRequired || 50}%</p>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Allowed Backlogs</span>
                    <p className="font-bold text-slate-800 mt-0.5">{selectedBatchDetails.metadata?.passingCriteria?.maxBacklogsAllowed || 2}</p>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Max Failed Limit</span>
                    <p className="font-bold text-slate-800 mt-0.5">{selectedBatchDetails.metadata?.passingCriteria?.maxFailedSubjects || 2}</p>
                  </div>
                </div>
              </div>

              {/* Students Ledger Table Context */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Candidate Specific Records ({selectedBatchDetails.students?.length || 0})</h4>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm block sm:table">
                    <thead className="bg-slate-50 hidden sm:table-header-group">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500">USN Number</th>
                        <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500">Student Name</th>
                        <th className="px-4 py-2.5 text-center text-xs font-bold text-slate-500">Roster Decision</th>
                        <th className="px-4 py-2.5 text-center text-xs font-bold text-slate-500">Aggregate %</th>
                        <th className="px-4 py-2.5 text-center text-xs font-bold text-slate-500">Failed Courses</th>
                        <th className="px-4 py-2.5 text-center text-xs font-bold text-slate-500">Status</th>
                        <th className="px-4 py-2.5 text-center text-xs font-bold text-slate-500">Management</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white block sm:divide-y sm:table-row-group">
                      {selectedBatchDetails.students?.map((student) => {
                        const decisionInfo = getDecisionLabel(student.decision);
                        const statusInfo = getStatusLabel(student.status);
                        const isRollbackAllowed = student.isPromoted || student.isPublished || student.isProvisional;
                        const canDownload = isStudentPublished(student);
                        const studentDownloadContext = {
                          programTypeId: selectedBatchDetails.metadata?.programTypeId,
                          programId: selectedBatchDetails.metadata?.programId,
                          academicYearId: selectedBatchDetails.metadata?.academicYearId,
                          levelId: selectedBatchDetails.metadata?.levelId || selectedBatchDetails.metadata?.termId || selectedBatchDetails.metadata?.semesterId,
                          isSemesterBased: selectedBatchDetails.metadata?.isSemesterBased || false,
                          levelName: selectedBatchDetails.metadata?.levelName || 'N/A'
                        };
                        
                        return (
                          <tr key={student.studentId} className="hover:bg-slate-50/50 transition block sm:table-row p-4 space-y-1 sm:space-y-0 border-b border-slate-100 sm:border-none">
                            <td className="px-4 py-0.5 sm:py-2.5 font-mono text-xs font-bold text-slate-700 block sm:table-cell">
                              <span className="sm:hidden font-sans font-bold text-slate-400 block text-[10px] uppercase">USN</span>
                              {student.studentUSN}
                            </td>
                            <td className="px-4 py-0.5 sm:py-2.5 text-sm font-semibold text-slate-800 block sm:table-cell">
                              <span className="sm:hidden font-sans font-bold text-slate-400 block text-[10px] uppercase">Candidate</span>
                              {student.studentName}
                            </td>
                            <td className="px-4 py-0.5 sm:py-2.5 sm:text-center block sm:table-cell">
                              <span className="sm:hidden font-sans font-bold text-slate-400 block text-[10px] uppercase mb-0.5">Decision</span>
                              <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold border ${decisionInfo.color}`}>
                                {decisionInfo.label}
                              </span>
                            </td>
                            <td className="px-4 py-0.5 sm:py-2.5 sm:text-center font-bold text-slate-800 block sm:table-cell">
                              <span className="sm:hidden font-sans font-bold text-slate-400 block text-[10px] uppercase">Aggregate</span>
                              {student.overallPercentage || 0}%
                            </td>
                            <td className="px-4 py-0.5 sm:py-2.5 sm:text-center font-bold text-slate-600 block sm:table-cell">
                              <span className="sm:hidden font-sans font-bold text-slate-400 block text-[10px] uppercase">Failed Count</span>
                              {student.failedSubjectsCount || 0}
                            </td>
                            <td className="px-4 py-0.5 sm:py-2.5 sm:text-center block sm:table-cell">
                              <span className="sm:hidden font-sans font-bold text-slate-400 block text-[10px] uppercase mb-0.5">State</span>
                              <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold border ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                            </td>
                            <td className="px-4 py-1 sm:py-2.5 sm:text-center block sm:table-cell">
                              <span className="sm:hidden font-sans font-bold text-slate-400 block text-[10px] uppercase mb-1">Actions</span>
                              <div className="flex flex-row sm:flex-col lg:flex-row items-center sm:justify-center gap-2">
                                {canDownload && (
                                  <ResultDownloadButton
                                    tenantId={tenantId}
                                    context={studentDownloadContext}
                                    studentId={student.studentId}
                                    batchId={selectedBatchDetails.batchId}
                                    isBatch={false}
                                    isPublished={true}
                                    buttonText="Download file"
                                    buttonVariant="outline"
                                    className="text-xs px-2 py-1 flex-1 sm:flex-none"
                                    onDownloadStart={() => console.log('File request:', student.studentUSN)}
                                    onDownloadComplete={() => console.log('File ready:', student.studentUSN)}
                                    onDownloadError={(err) => console.error('File issue:', err)}
                                  />
                                )}
                                {isRollbackAllowed && (
                                  <button
                                    onClick={() => handleRollbackIndividual(student.studentId, selectedBatchDetails.batchId)}
                                    disabled={loading}
                                    className="text-xs font-bold text-rose-600 hover:text-rose-800 disabled:opacity-40 transition py-1 px-2 border border-transparent hover:border-rose-200 rounded-lg"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 border-t border-slate-200 px-4 py-3.5 flex justify-end flex-shrink-0">
              <button
                onClick={() => {
                  setShowBatchDetails(false);
                  setSelectedBatchDetails(null);
                }}
                className="w-full sm:w-auto rounded-xl bg-white border border-slate-300 px-5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
              >
                Close Ledger
              </button>
            </div>
          </div>
        </div>
      )}

      {renderConfirmModal()}
    </div>
  );
};

export default PromotionBatches;